// Detection, parsing, and plain-text fallback for BriefDocument.
//
// Persisted as a stringified JSON document on `content_briefs.draft_body`. We
// intentionally do not change the column type; legacy plain-text drafts are
// detected by `looksLikeBriefDocument()` and left untouched.

import type { BlockNode, BriefDocument } from "./types";

/** Cheap structural sniff — true if the string is plausibly a BriefDocument JSON blob. */
export const looksLikeBriefDocument = (raw: string | null | undefined): boolean => {
  if (!raw) return false;
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith("{")) return false;
  return trimmed.includes('"doc"') || trimmed.includes('"type"');
};

/** Parse a stored draft_body. Returns `null` for plain-text or malformed JSON. */
export const parseBriefDocument = (raw: string | null | undefined): BriefDocument | null => {
  if (!looksLikeBriefDocument(raw)) return null;
  try {
    const parsed: unknown = JSON.parse(raw as string);
    if (
      parsed &&
      typeof parsed === "object" &&
      (parsed as { type?: unknown }).type === "doc" &&
      Array.isArray((parsed as { content?: unknown }).content)
    ) {
      return parsed as BriefDocument;
    }
    return null;
  } catch {
    return null;
  }
};

export const emptyBriefDocument = (): BriefDocument => ({ type: "doc", content: [] });

/**
 * Convert a legacy plain-text draft into a minimal BriefDocument.
 *
 * Heuristics:
 *   - Blank-line separators → paragraph boundaries.
 *   - Lines starting with `## ` → h2; `### ` → h3.
 *   - Lines starting with `> ` → blockquote (collapsed into a single block).
 *   - No detection of footnotes, callouts, or pull-quotes — these are too lossy
 *     and require explicit user intent.
 */
export const plainTextToBriefDocument = (text: string): BriefDocument => {
  const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  const content: BlockNode[] = [];
  for (const raw of blocks) {
    const block = raw.trim();
    if (!block) continue;
    if (block.startsWith("### ")) {
      content.push({ type: "heading", attrs: { level: 3 }, content: [{ type: "text", text: block.slice(4) }] });
    } else if (block.startsWith("## ")) {
      content.push({ type: "heading", attrs: { level: 2 }, content: [{ type: "text", text: block.slice(3) }] });
    } else if (block.startsWith("> ")) {
      const lines = block.split("\n").map((l) => (l.startsWith("> ") ? l.slice(2) : l));
      content.push({
        type: "blockquote",
        content: [{ type: "paragraph", content: [{ type: "text", text: lines.join(" ") }] }],
      });
    } else {
      content.push({ type: "paragraph", content: [{ type: "text", text: block }] });
    }
  }
  return { type: "doc", content };
};
